import { useState, useEffect, useRef } from "react";
import { useUser } from "../context/userContext";
import { Link, ScrollRestoration, useNavigate } from "react-router-dom";
import axios from "axios";
import NavBar from "../components/NavBar/navBar.component";
import Footer from "../components/Footer/footer.component";
import { ReactComponent as InfoIcon } from "../assets/icons/info_icon.svg";

const SignupPage = () => {
  const { setUserData } = useUser();

  const [isContentLoading, setIsContentLoading] = useState(false);

  const initialValues = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobile_number: "",
    agreedToTerms: false,
  };

  const [generateOtpSuccess, setGenerateOtpSuccess] = useState(false);

  const inputs = useRef([]);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [resendActive, setResendActive] = useState(true);
  const [timer, setTimer] = useState(60);
  const [resendCount, setResendCount] = useState(0);

  const [otpSuccess, setOtpSuccess] = useState(false);
  const [formData, setFormData] = useState(initialValues);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState(null);
  const [agreedToTermsError, setAgreedToTermsError] = useState(null);

  const navigate = useNavigate();

  const handleInput = (index, event) => {
    const value = event.target.value;

    // Validate input for numbers
    if (!isNaN(value)) {
      // Clear any existing error
      setOtpError(false);

      // Update the otp state with the new digit
      setOtp((prevOtp) => {
        const updatedOtp = [...prevOtp];
        updatedOtp[index] = value;
        return updatedOtp.join(""); // Combine the array of digits into a string
      });

      // Move focus to the next input field if available
      const nextInput = inputs.current[index + 1];
      if (nextInput && value) {
        nextInput.focus();
      }
    } else {
      // Set an error if a non-numeric value is entered
      setOtpError(true);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text/plain");
    const pasteDigits = pasteData.split("").filter((char) => !isNaN(char));

    inputs.current.forEach((input, index) => {
      if (pasteDigits[index]) {
        input.value = pasteDigits[index];
        handleInput(index, { target: { value: pasteDigits[index] } });
      }
    });
  };

  const handleGenerateOtp = async (e) => {
    e.preventDefault();
    // Email validation using regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email');
      return;
    }

    // Clear any previous email error
    setError(false);

    try {
      setIsContentLoading(true);
      const response = await axios.post("https://soliboat.solidaridadasia.com/signup_email_verification", {
        email: formData.email,
      });
      console.log(response)
      setGenerateOtpSuccess(true);
    } catch (error) {
      alert(`Error sending OTP: ${error.response.data.errors[0]}`);
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleResendCode = async (e) => {
    if (resendCount < 2) {
      setResendActive(false);
      setTimer(60);
      setResendCount(resendCount + 1);
      await handleGenerateOtp(e);
      setTimeout(() => {
        setResendActive(true);
      }, 60000);
    }
  };

  useEffect(() => {
    let interval;
    if (resendActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendActive, timer]);

  const handleValidateOTP = async (e) => {
    e.preventDefault();
    try {
      setIsContentLoading(true);

      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/validate_signup_otp",
        {
          email: formData.email,
          otp: otp,
        }
      )
      console.log(response?.data?.message[0])
      if(response?.data?.message[0] === 'OTP validity expired. Regenerate OTP'){
        setOtpSuccess(false);
        alert(`Invalid OTP or OTP validity expired. Please regenerate OTP`);
        // setOtpError(true);
        return
      } else {
        setOtpSuccess(true);
      }
      
    } catch (error) {
      setOtpError(true);
      console.error("Error validating OTP:", error);
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Clear previous error messages
    setError(null);
    setPhoneError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setAgreedToTermsError(null);

    // Validation logic - you can add more validation as needed
    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.email ||
      !formData.mobile_number ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    // Password validation (example: at least 6 characters)
    if (formData.password.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      setConfirmPasswordError("Passwords do not match.");
      return;
    }

    // Phone number validation using a simple regex (example: 10 digits)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.mobile_number)) {
      setPhoneError("Invalid phone number.");
      return;
    }

    // Check if the checkbox is checked
    if (!formData.agreedToTerms) {
      setAgreedToTermsError(
        "You must agree to the Terms and Conditions and Privacy Policy."
      );
      return;
    }

    try {
      setIsContentLoading(true);

      // Make a POST request using Axios
      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/sign-up",
        formData
      );

      setUserData((prevUserData) => ({
        ...prevUserData,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        mobile_number: formData.mobile_number,
        session_data: [],
        session_id: [],
        token: response.data.data[0].token,
      }));

      alert(response.data.message[0]);

      setFormData(initialValues);

      navigate("/dashboard");
    } catch (error) {
      alert(error.response.data.errors[0]);
      setError("Error creating account. Please try again.");
    } finally {
      setIsContentLoading(false);
    }
  };

  return (
    <>
      {/* Loading screen while API calls are in progress */}
      {isContentLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-blue-100/70 z-[100]">
          <div className="w-20 h-20 animate-spin">
            <div className="h-full w-full border-4 border-t-dark/50 border-b-dark/80 rounded-[50%]" />
          </div>
        </div>
      )}
      <ScrollRestoration />
      <NavBar />
      <section className="relative z-10 overflow-hidden pt-36 pb-16 md:pb-20 lg:pt-[180px] lg:pb-28">
        <div className="container">
          <div className="-mx-4 flex flex-wrap">
            <div className="w-full px-4">
              <div className="mx-auto max-w-[500px] rounded-md bg-primary bg-opacity-5 py-10 px-6 dark:bg-dark sm:p-[60px]">
                <h3 className="mb-3 text-center text-2xl font-bold text-black dark:text-white sm:text-3xl">
                  Create your account
                </h3>
                <p className="mb-8 text-center text-base font-medium text-body-color">
                  {!generateOtpSuccess && (
                    <>
                      Please enter a valid email address to receive a
                      verification code.
                    </>
                  )}
                  {generateOtpSuccess && !otpSuccess && (
                    <>
                      Please enter the 6 digit code sent to your <br /> email.
                    </>
                  )}
                  {generateOtpSuccess && otpSuccess && (
                    <>It’s totally free and super easy</>
                  )}
                </p>
                <form>
                  {/* Form fields */}
                  {!generateOtpSuccess && (
                    <div className="mb-8">
                      <label
                        htmlFor="email"
                        className="mb-3 block text-sm font-medium text-dark dark:text-white"
                      >
                        {" "}
                        Email{" "}
                      </label>
                      <input
                        type="email"
                        name="email"
                        placeholder="Enter your Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-transparent py-3 px-6 text-base text-body-color placeholder-body-color shadow-one outline-none focus:border-primary focus-visible:shadow-none dark:bg-[#242B51] dark:shadow-signUp"
                        required
                      />
                      {error && (
                          <div className="flex items-center mt-4 text-red">
                            <InfoIcon className="w-6 h-6" />
                            <span className="ml-2">{error}</span>
                          </div>
                        )}
                    </div>
                  )}
                  {generateOtpSuccess && !otpSuccess && (
                    <>
                      <div className="mt-2 mb-6">
                        <div class="flex justify-center items-center space-x-4">
                          {[...Array(6)].map((_, index) => (
                            <input
                              key={index}
                              ref={(el) => (inputs.current[index] = el)}
                              type="text"
                              maxLength="1"
                              className="w-8 h-8 md:w-10 md:h-10 border border-gray-300 rounded text-center text-xl focus:outline-none focus:border-blue-500"
                              onInput={(e) => handleInput(index, e)}
                              onPaste={handlePaste}
                            />
                          ))}
                        </div>

                        {otpError && (
                          <div className="flex justify-center items-center mt-2 md:mt-3">
                            <InfoIcon className="w-5 h-5" />
                            <span className="text-red text-sm ml-2">
                              Please enter only numeric values.
                            </span>
                          </div>
                        )}

                        <p className="mt-4 text-center text-sm text-neutral-700">
                          The OTP expires in{" "}
                          <span className="font-semibold">5 minutes</span>.
                        </p>
                      </div>
                    </>
                  )}

                  {generateOtpSuccess && otpSuccess && (
                    <>
                      <div className="mb-8">
                        <label
                          htmlFor="name"
                          className="mb-3 block text-sm font-medium text-dark dark:text-white"
                        >
                          {" "}
                          First Name{" "}
                        </label>
                        <input
                          type="text"
                          name="first_name"
                          placeholder="Enter your first name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          className="w-full rounded-md border border-transparent py-3 px-6 text-base text-body-color placeholder-body-color shadow-one outline-none focus:border-primary focus-visible:shadow-none dark:bg-[#242B51] dark:shadow-signUp"
                          required
                        />
                      </div>
                      <div className="mb-8">
                        <label
                          htmlFor="name"
                          className="mb-3 block text-sm font-medium text-dark dark:text-white"
                        >
                          {" "}
                          Last Name{" "}
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          placeholder="Enter your last name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          className="w-full rounded-md border border-transparent py-3 px-6 text-base text-body-color placeholder-body-color shadow-one outline-none focus:border-primary focus-visible:shadow-none dark:bg-[#242B51] dark:shadow-signUp"
                          required
                        />
                      </div>
                      <div className="mb-8">
                        <label
                          htmlFor="tel"
                          className="mb-3 block text-sm font-medium text-dark dark:text-white"
                        >
                          {" "}
                          Phone No.{" "}
                        </label>
                        <input
                          type="tel"
                          name="mobile_number"
                          placeholder="Enter your Phone No."
                          value={formData.mobile_number}
                          onChange={handleInputChange}
                          className="w-full rounded-md border border-transparent py-3 px-6 text-base text-body-color placeholder-body-color shadow-one outline-none focus:border-primary focus-visible:shadow-none dark:bg-[#242B51] dark:shadow-signUp"
                          required
                        />
                        {/* Display error messages */}
                        {phoneError && (
                          <div className="flex items-center mt-4 text-red">
                            <InfoIcon className="w-6 h-6" />
                            <span className="ml-2 text-sm">{phoneError}</span>
                          </div>
                        )}
                      </div>
                      <div className="mb-8">
                        <label
                          htmlFor="password"
                          className="mb-3 block text-sm font-medium text-dark dark:text-white"
                        >
                          {" "}
                          Your Password{" "}
                        </label>
                        <input
                          type="password"
                          name="password"
                          placeholder="Enter your Password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full rounded-md border border-transparent py-3 px-6 text-base text-body-color placeholder-body-color shadow-one outline-none focus:border-primary focus-visible:shadow-none dark:bg-[#242B51] dark:shadow-signUp"
                          required
                        />
                        {/* Display error message */}
                        {passwordError && (
                          <div className="flex items-center mt-4 text-red">
                            <InfoIcon className="w-6 h-6" />
                            <span className="ml-2 text-sm">
                              {passwordError}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="mb-8">
                        <label
                          htmlFor="confirmPassword"
                          className="mb-3 block text-sm font-medium text-dark dark:text-white"
                        >
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          placeholder="Confirm your Password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="w-full rounded-md border border-transparent py-3 px-6 text-base text-body-color placeholder-body-color shadow-one outline-none focus:border-primary focus-visible:shadow-none dark:bg-[#242B51] dark:shadow-signUp"
                          required
                        />
                        {/* Display error message */}
                        {confirmPasswordError && (
                          <div className="flex items-center mt-4 text-red">
                            <InfoIcon className="w-6 h-6" />
                            <span className="ml-2 text-sm">
                              {confirmPasswordError}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mb-8 flex flex-col">
                        <label
                          htmlFor="checkboxLabel"
                          className="flex cursor-pointer select-none text-sm font-medium text-body-color"
                        >
                          <div className="relative">
                            <input
                              type="checkbox"
                              id="checkboxLabel"
                              name="agreedToTerms"
                              checked={formData.agreedToTerms}
                              onChange={handleInputChange}
                              className="sr-only"
                            />

                            <div className="box mr-4 mt-1 flex h-5 w-5 items-center justify-center rounded border border-body-color border-opacity-80 dark:border-white dark:border-opacity-10">
                              <span className="opacity-0">
                                <svg
                                  width="11"
                                  height="8"
                                  viewBox="0 0 11 8"
                                  fill="none"
                                  xmlns="https://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                                    fill="#3056D3"
                                    stroke="#3056D3"
                                    strokeWidth="0.4"
                                  />
                                </svg>
                              </span>
                            </div>
                          </div>

                          <span>
                            By creating an account, you agree to the{" "}
                            <Link
                              to="/termsofservice"
                              className="text-primary hover:underline"
                              onClick={() => window.scrollTo({ top: 0 })}
                            >
                              Terms of Service
                            </Link>
                            , and our{" "}
                            <Link
                              to="/privacypolicy"
                              className="text-primary hover:underline"
                              onClick={() => window.scrollTo({ top: 0 })}
                            >
                              Privacy Policy{" "}
                            </Link>
                          </span>
                        </label>

                        {/* Display error message */}
                        {agreedToTermsError && (
                          <div className="flex items-center mt-4 text-red">
                            <InfoIcon className="w-6 h-6" />
                            <span className="ml-2 text-sm">
                              {agreedToTermsError}
                            </span>
                          </div>
                        )}

                        {/* Display error message */}
                        {error && (
                          <div className="flex items-center mt-4 text-red">
                            <InfoIcon className="w-6 h-6" />
                            <span className="ml-2">{error}</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="mb-6">
                    {!generateOtpSuccess && (
                      <button
                        className="flex w-full items-center justify-center rounded-md bg-primary py-4 px-9 text-base font-medium text-white transition duration-300 ease-in-out hover:bg-opacity-80 hover:shadow-signUp"
                        onClick={handleGenerateOtp}
                      >
                        Get Code
                      </button>
                    )}
                    {generateOtpSuccess && !otpSuccess && (
                      <button
                        className="flex w-full items-center justify-center rounded-md bg-primary py-4 px-9 text-base font-medium text-white transition duration-300 ease-in-out hover:bg-opacity-80 hover:shadow-signUp"
                        onClick={handleValidateOTP}
                      >
                        Validate Code
                      </button>
                    )}
                    {generateOtpSuccess && otpSuccess && (
                      <button
                        onClick={handleFormSubmit}
                        className="flex w-full items-center justify-center rounded-md bg-primary py-4 px-9 text-base font-medium text-white transition duration-300 ease-in-out hover:bg-opacity-80 hover:shadow-signUp"
                      >
                        Sign up
                      </button>
                    )}
                  </div>
                </form>

                {!generateOtpSuccess && (
                  <p className="text-center text-base font-medium text-body-color">
                    Already using XplorEase?
                    <Link
                      to="/signin"
                      className="text-primary hover:underline ml-1"
                    >
                      Sign in
                    </Link>
                  </p>
                )}{" "}
                {generateOtpSuccess && !otpSuccess && (
                  <p className="text-center text-base font-medium text-body-color">
                    Didn't get the otp?{" "}
                    <button
                      onClick={handleResendCode}
                      disabled={(resendActive && timer > 0) || resendCount >= 2}
                      className={`text-sm font-medium underline ${
                        timer > 0 || resendCount >= 2
                          ? "text-gray-500 cursor-not-allowed"
                          : "text-primary"
                      }`}
                    >
                      {resendActive && resendCount < 2
                        ? `Resend Code${
                            timer > 0 ? ` (in ${timer} seconds).` : ""
                          }`
                        : "Resend Code (Limit Exceeded)"}
                    </button>
                  </p>
                )}
                {generateOtpSuccess && otpSuccess && (
                  <p className="text-center text-base font-medium text-body-color">
                    Already using XplorEase?
                    <Link
                      to="/signin"
                      className="text-primary hover:underline ml-1"
                    >
                      Sign in
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute left-0 top-0 z-[-1]">
          <svg
            width="1440"
            height="969"
            viewBox="0 0 1440 969"
            fill="none"
            xmlns="https://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_95:1005"
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="1440"
              height="969"
            >
              <rect width="1440" height="969" fill="#090E34" />
            </mask>
            <g mask="url(#mask0_95:1005)">
              <path
                opacity="0.1"
                d="M1086.96 297.978L632.959 554.978L935.625 535.926L1086.96 297.978Z"
                fill="url(#paint0_linear_95:1005)"
              />
              <path
                opacity="0.1"
                d="M1324.5 755.5L1450 687V886.5L1324.5 967.5L-10 288L1324.5 755.5Z"
                fill="url(#paint1_linear_95:1005)"
              />
            </g>
            <defs>
              <linearGradient
                id="paint0_linear_95:1005"
                x1="1178.4"
                y1="151.853"
                x2="780.959"
                y2="453.581"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4A6CF7" />
                <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_95:1005"
                x1="160.5"
                y1="220"
                x2="1099.45"
                y2="1192.04"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4A6CF7" />
                <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default SignupPage;
